import { ScrollArea } from "@/components/ui/scroll-area";
import type { ReactNode } from "react";
import { useAccountStore } from "@/features/account-management";
import { cn } from "@/lib/cn";
import { useGetThemesQuery } from "@/shared/services";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { type StorydocTheme, storydocThemes } from "../data/storydoc-themes";
import { useDesignSystemStore } from "../../../store/design-system.store";
import ThemeListItem from "./theme-list-item";

const SKELETON_STYLE =
  "[background:linear-gradient(90deg,_#e7e6ec_0%,_#f2f1f7_100%)]";

const STORYDOC_COLOR_KEYS = [
  "primaryColor",
  "secondaryColor",
  "themeColor3",
  "themeColor4",
] as const;

const ThemesSelectPopover = ({
  isOpen,
  close,
  onSelectTheme,
  leadingItem,
}: {
  isOpen: boolean;
  close: () => void;
  // When provided, picking a theme reports its id instead of applying it to the
  // editor design-system store. Lets non-editor surfaces reuse this popover.
  onSelectTheme?: (themeId: string) => void;
  /** Rendered first under "Your themes" — e.g. the funnel's synthesized Brand theme row. */
  leadingItem?: ReactNode;
}) => {
  const [parent] = useAutoAnimate();
  const { data: themes, isLoading } = useGetThemesQuery({ enabled: isOpen });
  const account = useAccountStore((s) => s.account);
  const defaultThemeId = account?.dsId;

  return (
    <div ref={parent}>
      {isLoading && <Skeleton key="skeleton" />}
      {!isLoading && (
        <ScrollArea className="tw-size-full tw-px-4" maxHeight={400} key="list">
          {(leadingItem || (themes && themes.length > 0)) && (
            <>
              <SectionLabel>Your themes</SectionLabel>
              {leadingItem}
              {(themes ?? []).map((theme) => (
                <ThemeListItem
                  key={theme._id}
                  theme={theme}
                  isDefault={defaultThemeId === theme._id}
                  close={close}
                  onSelectTheme={onSelectTheme}
                />
              ))}
            </>
          )}
          <SectionLabel>Storydoc themes</SectionLabel>
          {storydocThemes.map((theme) => (
            <StorydocThemeItem
              key={theme.id}
              theme={theme}
              close={close}
              onSelectTheme={onSelectTheme}
            />
          ))}
        </ScrollArea>
      )}
    </div>
  );
};

export default ThemesSelectPopover;

const StorydocThemeItem = ({
  theme,
  close,
  onSelectTheme,
}: {
  theme: StorydocTheme;
  close: () => void;
  onSelectTheme?: (themeId: string) => void;
}) => {
  const update = useDesignSystemStore((s) => s.update);

  const handleApply = () => {
    if (onSelectTheme) {
      onSelectTheme(theme.id);
      close();
      return;
    }
    update({
      primaryColor: theme.primaryColor,
      secondaryColor: theme.secondaryColor,
      themeColor3: theme.themeColor3,
      themeColor4: theme.themeColor4,
      backgroundColor: theme.backgroundColor,
      backgroundColor2: theme.backgroundColor2,
    });
    close();
  };

  return (
    <div
      onClick={handleApply}
      className="tw-[.theme-select-flyout_&]:tw-border-b-0 tw-group tw-cursor-pointer tw-border-b tw-border-solid tw-border-blue-100/50 tw-py-4 tw-transition-colors tw-duration-200 last:tw-border-b-0"
    >
      <div className="tw-mb-2 tw-font-sans tw-text-sm tw-font-medium tw-text-blue">
        {theme.title}
      </div>
      <div className="tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-p-3 tw-transition-colors tw-duration-200 hover:tw-bg-blue-50/50">
        <div className="tw-flex tw-items-center tw-gap-2">
          {STORYDOC_COLOR_KEYS.map((key) => (
            <span
              key={key}
              className="tw-block tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-blue/10"
              style={{ background: theme[key] }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="w-block tw-pb-1 tw-pt-3 tw-font-sans tw-text-xs tw-font-normal tw-text-blue-500 first:tw-mt-0">
    {children}
  </span>
);

const SKELETON_ROWS = ["row-a", "row-b", "row-c"];
const SKELETON_DOTS = ["dot-a", "dot-b", "dot-c", "dot-d"];
const Skeleton = () => (
  <div className="tw-p-4">
    {SKELETON_ROWS.map((rowId) => (
      <div
        className="tw-mb-3 tw-animate-skeleton-loading tw-rounded-lg tw-p-3 last:tw-mb-0"
        key={rowId}
      >
        <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
          <span
            className={cn(
              "tw-block tw-h-[13px] tw-w-24 tw-rounded",
              SKELETON_STYLE,
            )}
          />
        </div>
        <div className="tw-flex tw-items-center tw-gap-1">
          {SKELETON_DOTS.map((dotId) => (
            <span
              key={`${rowId}-${dotId}`}
              className={cn(
                "tw-block tw-size-6 tw-rounded-full",
                SKELETON_STYLE,
              )}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);
